import { z } from "zod";

/**
 * A product card as the *widget* sees it — every field here was written by
 * the server during tool-result hydration, never by the model. See
 * PLAN.md "Rich content is server-authoritative": the model picks a
 * `productId` and may add a `note`; title/price/image/url/stock always come
 * from the cached `search_products` result for that turn.
 */
export const productBlockItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  priceCents: z.number().int().nonnegative().optional(),
  currency: z.string().length(3).optional(),
  /**
   * `.catch(undefined)` on both link fields, because they are decoration
   * and the schema is also what *reads stored history back*. A catalogue
   * that yields a relative path (`/media/sku-1.jpg`) or an explicit `null`
   * would otherwise make every message carrying that card unparseable —
   * and since the widget's `session.ready` replays the transcript, one
   * such card permanently bricked the conversation for everyone in it.
   * A card without a picture is still a card; a conversation nobody can
   * open is not a conversation. Relative values are dropped rather than
   * passed through: they would resolve against the *client's* origin, not
   * the shop's, which is what `productImageTemplate` exists to fix.
   */
  imageUrl: z.string().url().optional().catch(undefined),
  /** Either a mapped field or built server-side from `product_url_template` — never model-authored. */
  url: z.string().url().optional().catch(undefined),
  stock: z.enum(["in_stock", "out_of_stock", "unknown"]).optional(),
  /** The model's one-line reason for surfacing this item — the only model-authored field on a card. */
  note: z.string().optional(),
});
export type ProductBlockItem = z.infer<typeof productBlockItemSchema>;

/**
 * Typed action intents (PLAN.md "Rich content is server-authoritative").
 * `open_url` is restricted server-side to the bot's allowed domains before
 * a block carrying one is ever emitted — the schema can't express that
 * constraint, so it's enforced where the block is built, not here.
 */
export const actionSchema = z.discriminatedUnion("intent", [
  z.object({ intent: z.literal("open_url"), label: z.string(), url: z.string().url() }),
  z.object({ intent: z.literal("quick_reply"), label: z.string(), value: z.string() }),
  z.object({ intent: z.literal("start_order"), label: z.string() }),
  z.object({ intent: z.literal("escalate"), label: z.string() }),
  z.object({ intent: z.literal("reset_session"), label: z.string() }),
  z.object({ intent: z.literal("show_products"), label: z.string(), productIds: z.array(z.string()) }),
]);
export type Action = z.infer<typeof actionSchema>;

export const formFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  kind: z.enum(["text", "email", "tel", "textarea"]),
  required: z.boolean().default(false),
});

/**
 * One tappable answer to a `followup` question. `value` is what actually
 * gets sent as the shopper's message when tapped — it defaults to `label`
 * server-side (`ask_followup`'s tool), so the model only has to write one
 * of them for the common case. `description` is optional context so the
 * shopper can choose between close options without asking back; when any
 * option in a group has one the widget renders the whole group as cards
 * instead of chips.
 */
export const followupOptionSchema = z.object({
  label: z.string().min(1).max(60),
  description: z.string().max(140).optional(),
  value: z.string().min(1),
});
export type FollowupOption = z.infer<typeof followupOptionSchema>;

/** `packages/shared` — the nine block types from PLAN.md "Widget". */
export const blockSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({ type: z.literal("products"), layout: z.enum(["carousel", "list"]), items: z.array(productBlockItemSchema) }),
  z.object({ type: z.literal("actions"), actions: z.array(actionSchema) }),
  z.object({ type: z.literal("links"), links: z.array(z.object({ label: z.string(), url: z.string().url() })) }),
  z.object({ type: z.literal("quick_replies"), replies: z.array(z.object({ label: z.string(), value: z.string() })) }),
  z.object({
    type: z.literal("order_confirm"),
    orderId: z.string(),
    items: z.array(z.object({ productId: z.string(), title: z.string(), quantity: z.number().int().positive(), priceCents: z.number().int().nonnegative() })),
    totalCents: z.number().int().nonnegative(),
    currency: z.string().length(3),
  }),
  z.object({ type: z.literal("form"), fields: z.array(formFieldSchema), submitLabel: z.string().optional() }),
  z.object({ type: z.literal("notice"), level: z.enum(["info", "warning", "error"]), text: z.string() }),
  /**
   * A single blocking question with a small, known set of answers
   * (PLAN.md "Ask one question at a time" — `ask_followup`'s whole
   * reason to exist). There is deliberately no "allow free text" flag:
   * the composer stays live for every message, so free text is an
   * invariant of the widget, not a per-block choice.
   */
  z.object({ type: z.literal("followup"), question: z.string().min(1), options: z.array(followupOptionSchema).min(2).max(5) }),
]);
export type Block = z.infer<typeof blockSchema>;
